'use client';

// Единый голосовой ввод для Composer и Generator: запись с микрофона (MediaRecorder),
// эквалайзер по реальному звуку (vizRef) и распознавание через transcribeUrl.
// Второй режим — голосовое сообщение (docs/MESSAGES-PLAN.md, М7): запись отдаётся
// блобом наружу (onBlob) и НЕ распознаётся — распознавание платное, а человеку в
// переписке уезжает сам звук.
// INVARIANT(scriptera): логика записи/распознавания живёт ТОЛЬКО здесь — не копировать
// в компоненты. Причина: 2 копии уже разъезжались (фиксы попадали в одну). Канон: docs/archive/AUDIT-FIX-PLAN.md#p2
import { useEffect, useRef, useState } from 'react';
import { blobToWavBase64 } from './wav';

export interface VoiceInput {
  recording: boolean;
  /** Идёт распознавание — блокировать ввод/кнопки. */
  transcribing: boolean;
  /** Контейнер полосок эквалайзера (рисуется компонентом при recording). */
  vizRef: React.RefObject<HTMLDivElement | null>;
  /** Первый вызов — старт записи, второй — стоп + распознать. */
  toggle: () => void;
  /** Прервать запись БЕЗ распознавания (крестик у нативного композера — Трек 2). */
  cancel: () => void;
}

interface Params {
  /** URL распознавания. POST {audioB64, mime} → { ok, text }. Пусто → toggle не делает ничего. */
  transcribeUrl?: string;
  /** Голосовое сообщение: готовая запись и её длительность в секундах. Задан — запись
   *  НЕ распознаётся (и transcribeUrl для старта не нужен). */
  onBlob?: (blob: Blob, seconds: number) => void;
  /** Распознанный текст (непустой). */
  onText: (text: string) => void;
  /** Микрофон недоступен / распознавание не удалось — показать юзеру, не молчать. */
  onError: () => void;
  /** Текущий уровень звука 0..1 на каждом кадре записи (родной композер рисует свою
   *  полосочку-эквалайзер — Трек 2; веб-эквалайзер по-прежнему идёт через vizRef). */
  onLevel?: (level: number) => void;
  /** Формат записи по выбору зовущего (лист записи голосового окна «Сообщение», ТЗ WEB-LOOK
   *  Э9 строка 7: webm/opus, иначе mp4). Пусто или браузер не умеет — формат браузера. */
  mimeType?: string;
}

export function useVoiceInput({ transcribeUrl, onBlob, onText, onError, onLevel, mimeType }: Params): VoiceInput {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const vizRef = useRef<HTMLDivElement | null>(null);
  const aliveRef = useRef(true);
  // Начало записи — для длительности голосового (MediaRecorder её не сообщает).
  const startedAtRef = useRef(0);
  // Колбэки в ref — onstop асинхронный, пропсы могли смениться к моменту вызова.
  const cbRef = useRef({ onText, onError, onLevel, onBlob });
  useEffect(() => {
    cbRef.current = { onText, onError, onLevel, onBlob };
  });

  function releaseHardware() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    void audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }

  // Размонтирование во время записи: гасим микрофон/рекордер, иначе индикатор
  // записи в браузере остаётся включённым навсегда.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      const mr = recRef.current;
      if (mr && mr.state !== 'inactive') {
        mr.onstop = null; // распознавать уже некому
        try {
          mr.stop();
        } catch {
          /* уже остановлен */
        }
      }
      recRef.current = null;
      releaseHardware();
    };
  }, []);

  async function start() {
    // Куда деть запись, должно быть известно заранее: распознавание либо голосовое.
    if ((!transcribeUrl && !cbRef.current.onBlob) || typeof navigator === 'undefined' || !navigator.mediaDevices) {
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Индикатор громкости (эквалайзер) по реальному звуку с микрофона.
      try {
        const AC: typeof AudioContext =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new AC();
        audioCtxRef.current = ctx;
        const an = ctx.createAnalyser();
        an.fftSize = 256;
        ctx.createMediaStreamSource(stream).connect(an);
        const data = new Uint8Array(an.frequencyBinCount);
        const tick = () => {
          an.getByteFrequencyData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) sum += data[i] ?? 0;
          const avg = sum / data.length / 255; // 0..1
          const lvl = Math.min(1, avg * 3); // усиление: речь обычно даёт небольшой avg
          cbRef.current.onLevel?.(lvl); // родной композер (Трек 2) рисует полосочку по уровню
          const bars = vizRef.current?.children;
          if (bars) {
            for (let i = 0; i < bars.length; i++) {
              const idle = 0.2 + 0.18 * Math.sin(Date.now() / 110 + i * 0.7); // лёгкая волна в покое
              const h = Math.max(0.22, Math.min(1, lvl + idle));
              (bars[i] as HTMLElement).style.transform = `scaleY(${h})`;
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        /* без визуализации */
      }
      const mr = recorderFor(stream, mimeType);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      mr.onstop = () => void finish(mr);
      recRef.current = mr;
      startedAtRef.current = Date.now();
      mr.start();
      setRecording(true);
    } catch {
      // нет доступа к микрофону
      releaseHardware();
      if (aliveRef.current) {
        setRecording(false);
        cbRef.current.onError();
      }
    }
  }

  // Запись закончилась: голосовое отдаём блобом как есть, иначе распознаём.
  async function finish(mr: MediaRecorder) {
    const seconds = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
    releaseHardware();
    if (!aliveRef.current) return;
    setRecording(false);
    const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
    if (!blob.size) return;
    const toBlob = cbRef.current.onBlob;
    if (toBlob) {
      toBlob(blob, seconds);
      return;
    }
    await transcribe(blob);
  }

  async function transcribe(blob: Blob) {
    setTranscribing(true);
    // Перекодируем в WAV (Gemini не ест webm/mp4). Если не вышло — шлём как есть.
    let audioB64 = '';
    let mime = 'audio/wav';
    try {
      audioB64 = await blobToWavBase64(blob);
    } catch {
      const dataUrl: string = await new Promise((res) => {
        const fr = new FileReader();
        fr.onloadend = () => res(String(fr.result));
        fr.readAsDataURL(blob);
      });
      audioB64 = dataUrl.split(',')[1] ?? '';
      mime = blob.type || 'audio/webm';
    }
    let text = '';
    try {
      const r = await fetch(transcribeUrl!, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ audioB64, mime }),
      });
      const j = (await r.json()) as { ok?: boolean; text?: string };
      if (j?.ok && j.text) text = j.text.trim();
    } catch {
      /* провал → onError ниже */
    }
    if (!aliveRef.current) return;
    setTranscribing(false);
    if (text) cbRef.current.onText(text);
    else cbRef.current.onError();
  }

  /** Рекордер в заказанном формате; браузер его не умеет — в своём (запись важнее формата). */
  function recorderFor(stream: MediaStream, mime?: string): MediaRecorder {
    if (mime && typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(mime)) {
      try {
        return new MediaRecorder(stream, { mimeType: mime });
      } catch {
        /* ниже — формат браузера */
      }
    }
    return new MediaRecorder(stream);
  }

  function toggle() {
    if (recording && recRef.current) {
      recRef.current.stop(); // → onstop → finish (голосовое наружу либо распознавание)
      return;
    }
    void start();
  }

  // Прервать без распознавания: снимаем onstop (транскрибировать нечего), гасим железо.
  function cancel() {
    const mr = recRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.onstop = null;
      try {
        mr.stop();
      } catch {
        /* уже остановлен */
      }
    }
    recRef.current = null;
    chunksRef.current = [];
    releaseHardware();
    if (aliveRef.current) setRecording(false);
  }

  return { recording, transcribing, vizRef, toggle, cancel };
}
